import { ChangeDetectorRef, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import EInvUomCodeJson from 'src/assets/data/einvoice-uom-code.json';

@Component({
  selector: 'app-einvoice-uom-code-select',
  styleUrls: ['./einvoice-uom-code-select.component.less'],
  templateUrl: './einvoice-uom-code-select.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EInvoiceUomCodeSelectComponent),
      multi: true
    }
  ]
})
export class EInvoiceUomCodeSelectComponent implements ControlValueAccessor {
  private originalOptions = [...EInvUomCodeJson];
  options = [...this.originalOptions];

  @Input() value: string = '';
  @Input() disabled = false;
  @Input() borderless = false;
  @Input() isRequired = false;

  get readonly() {
    if (this.disabled) {
      return true;
    }
    return false;
  }

  get displayText() {
    const item = this.options.find(x => x.Code === this.value);
    if (item) return `${item.Code} - ${item.Name}`;
    return '';
  }

  onChange: any = () => {};
  onTouched: any = () => {};
  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(value: string): void {
    this.value = value;
    this.onChange(value);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onModelChange(val: any) {
    this.value = val;
    this.onChange(val);
    this.cdr.markForCheck();
  }

  onSearch(val: string) {
    if (val) {
      const lowercaseVal = val.toLowerCase();

      // Filter and sort the options
      this.options = this.originalOptions
        .filter(
          item =>
            this.fuzzySearch(lowercaseVal, item.Code) ||
            this.fuzzySearch(lowercaseVal, item.Name)
        )
        .sort((a, b) => {
          // Check if Code starts with the search value
          if (
            a.Code.toLowerCase().startsWith(lowercaseVal) &&
            !b.Code.toLowerCase().startsWith(lowercaseVal)
          ) {
            return -1;
          }
          if (
            b.Code.toLowerCase().startsWith(lowercaseVal) &&
            !a.Code.toLowerCase().startsWith(lowercaseVal)
          ) {
            return 1;
          }

          // Check if Name starts with the search value
          if (
            a.Name.toLowerCase().startsWith(lowercaseVal) &&
            !b.Name.toLowerCase().startsWith(lowercaseVal)
          ) {
            return -1;
          }
          if (
            b.Name.toLowerCase().startsWith(lowercaseVal) &&
            !a.Name.toLowerCase().startsWith(lowercaseVal)
          ) {
            return 1;
          }

          // If neither starts with the search value, maintain original order
          return 0;
        });
    } else {
      this.options = [...this.originalOptions];
    }
    this.cdr.markForCheck();
  }

  /**
   * Performs a fuzzy search to determine if the needle is present in the haystack.
   *
   * @param needle The string to search for.
   * @param haystack The string to search in.
   * @returns True if the needle is found in the haystack, false otherwise.
   */
  private fuzzySearch(needle: string, haystack: string): boolean {
    // Convert both needle and haystack to lowercase for case-insensitive comparison
    needle = needle.toLowerCase();
    haystack = haystack.toLowerCase();

    // Get the lengths of the needle and haystack
    let hLen = haystack.length;
    let nLen = needle.length;

    // If the needle is longer than the haystack, it cannot be found
    if (nLen > hLen) {
      return false;
    }

    // If the needle and haystack have the same length, perform a direct comparison
    if (nLen === hLen) {
      return needle === haystack;
    }

    // Iterate through each character in the needle
    outer: for (let i = 0, j = 0; i < nLen; i++) {
      let nChar = needle.charCodeAt(i);

      // Iterate through each character in the haystack
      while (j < hLen) {
        // If a character in the haystack matches the current character in the needle, continue to the next character in the needle
        if (haystack.charCodeAt(j++) === nChar) {
          continue outer;
        }
      }

      // If no match is found for the current character in the needle, it cannot be found
      return false;
    }

    // If all characters in the needle are found in the haystack, it is considered a match
    return true;
  }
}
